import {MAX_QUERIES_TO_MARK_FOR_PREHEATING} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {type RefObject, useEffect, useMemo, useState} from 'react'

import {useCachePreheatIssueViewerQueries} from '../../hooks/use-cache-preheat-issueviewer'
import type {ItemIdentifier} from '../../types/issue'
import {gatherIssueLinks} from '../../utils/helpers'
import {parseGitHubIssueUrl} from '../../utils/urls'

type IssueLinkPreheatProps = {
  /** The rendered timeline, scanned for links to other issues. */
  timelineContainerRef: RefObject<HTMLDivElement | null>
  issueBodyRef?: RefObject<HTMLDivElement | null>
  issueUrl: string
  /** Rescan when the timeline grows. */
  totalItemCount: number
}

/**
 * Warms issue queries for links in the body and timeline using its viewer's preheat mode. A route without
 * a preheat scheduler can leave this component unmounted.
 */
export function IssueLinkPreheat({
  timelineContainerRef,
  issueBodyRef,
  issueUrl,
  totalItemCount,
}: IssueLinkPreheatProps) {
  const [issueLinks, setIssueLinks] = useState<string[]>([])

  useEffect(() => {
    const containers = [issueBodyRef?.current, timelineContainerRef.current].filter(
      (container): container is HTMLDivElement => container != null,
    )
    const updateLinks = () => {
      const links = containers.flatMap(gatherIssueLinks)
      setIssueLinks(current =>
        current.length === links.length && current.every((link, index) => link === links[index]) ? current : links,
      )
    }

    updateLinks()
    let pendingFrame: number | undefined
    const observer = new MutationObserver(() => {
      if (pendingFrame !== undefined) return
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = undefined
        updateLinks()
      })
    })
    for (const container of containers) {
      observer.observe(container, {childList: true, subtree: true, attributes: true, attributeFilter: ['href']})
    }
    return () => {
      observer.disconnect()
      if (pendingFrame !== undefined) cancelAnimationFrame(pendingFrame)
    }
  }, [totalItemCount, timelineContainerRef, issueBodyRef, issueUrl])

  const issuesWithLinks: Array<Omit<ItemIdentifier, 'type'>> = useMemo(() => {
    const currentIssue = parseGitHubIssueUrl(issueUrl)
    const seen = new Map<string, Omit<ItemIdentifier, 'type'>>()
    for (const link of issueLinks) {
      const parsed = parseGitHubIssueUrl(link)
      if (!parsed) continue
      // No need to preheat what we're already viewing
      if (
        currentIssue &&
        parsed.owner === currentIssue.owner &&
        parsed.repo === currentIssue.repo &&
        parsed.number === currentIssue.number
      ) {
        continue
      }
      const key = `${parsed.owner}/${parsed.repo}#${parsed.number}`
      if (!seen.has(key)) {
        seen.set(key, parsed)
        if (seen.size >= MAX_QUERIES_TO_MARK_FOR_PREHEATING) break
      }
    }
    return Array.from(seen.values())
  }, [issueLinks, issueUrl])

  useCachePreheatIssueViewerQueries({
    issues: issuesWithLinks,
    source: 'IssueTimeline',
  })

  return null
}
